import { isEqual } from 'underscore';
import { isNil } from 'lodash';

import { MentionSearchQueryModel } from './mention-search-query.model';
import { SearchCriteriaSortFieldEnum } from '../../../api/mentions/enum/search-criteria-sort-field.enum';
import { MentionSearchFacetTypesEnum } from '../../../api/mentions/enum/mention-search-facet-types.enum';
import { DateFacetModel } from '../../../api/mentions/model/date-facet.model';
import { SearchCriteriaModel } from '../../../api/mentions/model/search-criteria.model';
import { SavedMentionSearchApiModel } from '../../../api/mentions/model/saved-mention-search.api.model';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { DateFacetTypesEnum } from '../../../api/mentions/enum/date-facet-types.enum';
import { MediaTypeModel } from './media-type.model';
import { SentimentModel } from './sentiment.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { SearchContextsEnum } from '../enums/search-contexts.enum';
import { StaticComparisonHelper } from '../helpers/comparison/comparison.helper';
import { SearchCallerTypeEnum } from '../../../api/mentions/enum/search-caller-type.enum';
import { ProminenceByReachRequestModel } from '../../../api/mentions/model/prominence-by-reach-request.model';
import { NeoFacetRequestApiModel } from '../../../api/mentions/model/neo-facet-request-api.model';
import { SortOrderEnum } from '../../../api/shared/enums/sort-order.enum';
import { SortFunctionModel } from '../../../api/mentions/model/sort-function.model';

export class MentionSearchCriteriaModel {
    public static readonly fastFacets: MentionSearchFacetTypesEnum[] = [
        MentionSearchFacetTypesEnum.mediaType,
        MentionSearchFacetTypesEnum.sentiment,
    ];

    /** Facets for new coverage, searching mercury */
    public static readonly mercurySearchFacets: MentionSearchFacetTypesEnum[] = [
        MentionSearchFacetTypesEnum.language,
        MentionSearchFacetTypesEnum.mediaType,
        MentionSearchFacetTypesEnum.tweetType,
        MentionSearchFacetTypesEnum.sentiment,
        MentionSearchFacetTypesEnum.continent,
        MentionSearchFacetTypesEnum.country,
        MentionSearchFacetTypesEnum.province,
        MentionSearchFacetTypesEnum.city,
        MentionSearchFacetTypesEnum.county,
        MentionSearchFacetTypesEnum.outlet,
        MentionSearchFacetTypesEnum.contact,
        MentionSearchFacetTypesEnum.hashtags,
        MentionSearchFacetTypesEnum.linkedinType,
        MentionSearchFacetTypesEnum.blueskyType,
        MentionSearchFacetTypesEnum.broadcastType,
    ];

    /** Facets for saved coverage, searching the account */
    public static readonly accountSearchFacets: MentionSearchFacetTypesEnum[] = [
        MentionSearchFacetTypesEnum.language,
        MentionSearchFacetTypesEnum.mediaType,
        MentionSearchFacetTypesEnum.tweetType,
        MentionSearchFacetTypesEnum.sentiment,
        MentionSearchFacetTypesEnum.continent,
        MentionSearchFacetTypesEnum.country,
        MentionSearchFacetTypesEnum.province,
        MentionSearchFacetTypesEnum.city,
        MentionSearchFacetTypesEnum.county,
        MentionSearchFacetTypesEnum.outlet,
        MentionSearchFacetTypesEnum.contact,
        MentionSearchFacetTypesEnum.hashtags,
        MentionSearchFacetTypesEnum.tag,
        MentionSearchFacetTypesEnum.tagGroup,
        MentionSearchFacetTypesEnum.linkedinType,
        MentionSearchFacetTypesEnum.blueskyType,
        MentionSearchFacetTypesEnum.broadcastType,
    ];

    public static readonly recentCoverageFacets: MentionSearchFacetTypesEnum[] = [
        MentionSearchFacetTypesEnum.mediaType,
        MentionSearchFacetTypesEnum.tweetType,
        MentionSearchFacetTypesEnum.outlet,
        MentionSearchFacetTypesEnum.linkedinType,
        MentionSearchFacetTypesEnum.blueskyType,
        MentionSearchFacetTypesEnum.broadcastType,
    ];

    public static readonly allSentiments: SentimentModel[] = [
        new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive),
        new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral),
        new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative),
        new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable),
    ];


    dateFacets: DateFacetModel[];
    facets: MentionSearchFacetTypesEnum[];
    neoFacets: NeoFacetRequestApiModel[];
    page: number;
    pageSize: number;
    sortField: SearchCriteriaSortFieldEnum;
    sortFunction?: SortFunctionModel;
    sortOrder: SortOrderEnum;
    showSimilar: boolean;
    showSyndicates?: boolean;
    query: MentionSearchQueryModel;
    isAccountSearch: boolean;
    isMonitorSearch: boolean;
    isRecentCoverageSearch: boolean;
    isPreviousPeriod: boolean = false;
    isWordCloudRequest: boolean;
    isAdHocSearch: boolean = true; // Use false for saved topics only
    prominenceByReachRequest: ProminenceByReachRequestModel;
    filterQuery: MentionSearchQueryModel;
    filterSavedSearchId: number;
    savedFilterIds: number[];

    /**
     * Class constructor
     *
     * @param apiEntity
     * @param noDefaultDate  When TRUE, search date range will not be defined
     */
    constructor(
        apiEntity?: SavedMentionSearchApiModel | SearchCriteriaModel,
        accountSearch: boolean = false
    ) {
        this.isAccountSearch = accountSearch;

        if (apiEntity) {
            this.constructFromApiEntity(apiEntity);

        } else {
            this.constructDefault();
        }
    }

    static createFiltered(
        source: MentionSearchCriteriaModel,
        filters: MentionSearchCriteriaModel,
        combineFilters: boolean = true,
        includeDate: boolean = true
    ): MentionSearchCriteriaModel {
        const output = source?.clone();

        if (filters) {
            output.showSimilar = filters.showSimilar;
            if (filters.savedFilterIds?.length) {
                output.savedFilterIds = [...filters.savedFilterIds];
            }

            if (combineFilters) {
                output.query = MentionSearchQueryModel.createFiltered(source.query, filters.query, includeDate);
            } else {
                // most of search + filter merging should be done in the backend
                output.filterQuery = filters.query.clone();
            }
        }

        return output;
    }

    /**
     * @param base64 b64 encoded string of a stripped-down & serialized (backend) search criteria model.
     */
    static fromFilterQueryString(base64: string): {
        savedFilterIds: number[];
        search: MentionSearchCriteriaModel;
        seriesUniqueUuid: string;
    } {
        const model: {
            search: MentionSearchCriteriaModel;
            seriesUniqueUuid: string;
            savedFilterIds: number[];
        } = {
            search: null,
            seriesUniqueUuid: null,
            savedFilterIds: []
        };

        try {
            const apiModel = JSON.parse(decodeURIComponent(atob(base64)));
            model.search = new MentionSearchCriteriaModel(apiModel);
            model.search.isPreviousPeriod = apiModel.isPreviousPeriod;
            model.seriesUniqueUuid = apiModel.seriesUniqueUuid;
            model.savedFilterIds = apiModel.savedFilterIds;
        } catch (e) {
            console.log('e', e);
            // parsing json failed, could happen if the url is junk
            return null;
        }

        return model;
    }

    /**
     * @param model the model to by query-stringified
     *
     * The parameters omitted aren't relevant for filters, and just make the query string longer.
     */
    static toFilterQueryString(model: MentionSearchCriteriaModel, seriesUniqueUuid?: string): string {
        if (!model) {
            return null;
        }
        const modelToEncode: any = {
            query: model.query.getApiEntity(),
            filterQuery: model.filterQuery?.getApiEntity() || null,
            showSimilar: model.showSimilar,
            sortField: model.sortField,
            seriesUniqueUuid,
            savedFilterIds: model.savedFilterIds
        };

        if (model.isPreviousPeriod) {
            modelToEncode.isPreviousPeriod = true;
        }

        return btoa(
            encodeURIComponent(JSON.stringify(modelToEncode))
        );
    }

    private getSearchCallerType(): SearchCallerTypeEnum {
        const searchCallerType: SearchCallerTypeEnum = SearchCallerTypeEnum.topics;

        if (this.query.subscription?.include.length > 0) {
            return undefined;
        }

        if (this.query.folder.include.length > 0) {
            return undefined;
        }
        if (this.query.tag.include.length > 0) {
            return undefined;
        }
        if (this.query.tagGroup.include.length > 0) {
            return undefined;
        }
        if (this.query.sourceGroup.include.length > 0) {
            return undefined;
        }

        return searchCallerType;
    }

    /**
     * Gets the api entity version of this model
     */
    public getApiEntity(): SearchCriteriaModel {
        return {
            dateFacets: this.dateFacets.slice(),
            facets: this.facets,
            neoFacets: this.neoFacets ? this.neoFacets.slice() : [],
            includeProminenceFacets: this.facets?.length > 0 || this.dateFacets?.length > 0,
            isWordCloudRequest: this.isWordCloudRequest,
            query: this.query.getApiEntity(),
            page: this.page,
            pageSize: this.pageSize,
            showSimilar: this.showSimilar,
            showSyndicates: this.showSyndicates,
            sortField: this.sortField,
            sortFunction: this.sortFunction,
            sortOrder: this.sortOrder,
            searchCallerType: this.getSearchCallerType(),
            prominenceByReachRequest: this.prominenceByReachRequest,
            filterQuery: this.filterQuery? this.filterQuery.getApiEntity(true) : null,
            filterSavedSearchId: this.filterSavedSearchId,
            savedFilterIds: this.savedFilterIds,
            isAdHocSearch: this.isAdHocSearch,
        } as SearchCriteriaModel;
    }

    public clone(keepHydratedRegions: boolean = false): MentionSearchCriteriaModel {
        const clone = new MentionSearchCriteriaModel(this.getApiEntity(), this.isAccountSearch);

        // getApiEntity() dehydrates regions to raw ids; preserve the hydrated objects when requested.
        if (keepHydratedRegions && (this.query.includedRegions.length || this.query.excludedRegions.length)) {
            clone.query.includedRegions = this.query.includedRegions.map(region => region.clone());
            clone.query.excludedRegions = this.query.excludedRegions.map(region => region.clone());
            clone.query.rawIncludedRegions = this.query.rawIncludedRegions;
            clone.query.rawExcludedRegions = this.query.rawExcludedRegions;
        }

        return clone;
    }

    /**
     * Sets the query to explictly ask for all if the query was going to ask for all anyway
     *
     * The back end interprets an empty field in the query as "all", this causes a problem when we try to determine if we should show a
     * 0 facet results or not, because the logic for determining if we should set a 0 facet is if we requested it and didn't get a facet
     * result for it.  So this function checks to see if the back end would have interpreted the request as "all", if it would, then we
     * specifically ask for all.
     */
    public setMinimumQueryForFacets(): void {
        if (this.facets.length === 0 || !this.query) {
            return;
        }

        this.facets.forEach(
            facet => {
                switch (facet) {
                    case MentionSearchFacetTypesEnum.mediaType:
                        if (this.query.includedMediaTypes.length === 0) {
                            this.query.includedMediaTypes = MediaTypeModel.getAllMediaTypes();
                        }
                        break;

                    case MentionSearchFacetTypesEnum.sentiment:
                        if (this.query.sentiments.length === 0) {
                            this.query.sentiments = MentionSearchCriteriaModel.allSentiments;
                        }
                        break;
                }
            }
        );
    }

    /**
     * Determines whether or not the query exists
    */
    public hasQuery(): boolean {
        const hasQuery: boolean = this.query !== null && typeof this.query === 'object';

        return hasQuery;
    }

    /**
     * Determines whether or not the keyword groups in
     * the search query contain the provided search context
    */
    public hasMatchingSearchContext(searchContext: SearchContextsEnum): boolean {
        const hasQuery: boolean = this.hasQuery();
        const hasMatchingSearchContext: boolean = hasQuery && this.query.hasMatchingSearchContext(searchContext);

        return hasMatchingSearchContext;
    }

    /**
     * Constructs this model based on the provided api entity
    */
    private constructFromApiEntity(apiEntity: SavedMentionSearchApiModel | SearchCriteriaModel): void {
        const apiSearchCriteria: SearchCriteriaModel = apiEntity as SearchCriteriaModel;
        if ((apiEntity as SavedMentionSearchApiModel).id) { // SavedMentionSearchApiModel
            this.isAdHocSearch = false; // Set to false only for saved mention searches
        } else if (!isNil((apiEntity as SearchCriteriaModel).isAdHocSearch)) { // SearchCriteriaModel
            this.isAdHocSearch = (apiEntity as SearchCriteriaModel).isAdHocSearch;
        }
        if (apiEntity.facetSearch) {
            apiSearchCriteria.dateFacets = apiEntity.facetSearch.dateFacets;
            apiSearchCriteria.facets = apiEntity.facetSearch.facets;
            apiSearchCriteria.neoFacets = apiEntity.facetSearch.neoFacets;
        }

        if (apiEntity.prominenceByReachRequest) {
            this.prominenceByReachRequest = apiEntity.prominenceByReachRequest;
        }
        if (apiEntity.isWordCloudRequest) {
            this.isWordCloudRequest = apiEntity.isWordCloudRequest;
        }

        this.dateFacets = (apiSearchCriteria.dateFacets?.length > 0) ? apiSearchCriteria.dateFacets : this.getDefaultDateFacets();
        this.facets = apiSearchCriteria.facets || this.getDefaultFacets();
        this.neoFacets = apiSearchCriteria.neoFacets?.map(neoFacet => JSON.parse(JSON.stringify(neoFacet))) || [];
        this.page = apiSearchCriteria.page || 1;
        this.pageSize = apiEntity.pageSize || 25;
        this.sortField = apiEntity.sortField || SearchCriteriaSortFieldEnum.published;
        this.sortOrder = apiEntity.sortOrder || SortOrderEnum.descending;
        this.showSimilar = apiEntity.showSimilar;
        this.showSyndicates = apiEntity.showSyndicates;
        this.query = new MentionSearchQueryModel(apiEntity.query);
        this.filterQuery = apiEntity.filterQuery ? new MentionSearchQueryModel(apiEntity.filterQuery) : null;
        this.savedFilterIds = apiEntity.savedFilterIds;
    }

    /**
     * Constructs the default version of this model
     *
     * @param noDefaultDate  When TRUE, model will be built without a search date range defined
     */
    private constructDefault(): void {
        this.dateFacets = this.getDefaultDateFacets();
        this.facets = this.getDefaultFacets();
        this.neoFacets = [];
        this.page = 1;
        this.pageSize = 25;
        this.showSimilar = true;
        this.showSyndicates = true;
        this.sortField = SearchCriteriaSortFieldEnum.published;
        this.sortOrder = SortOrderEnum.descending;
        this.query = this.getDefaultQuery();
        this.filterQuery = null;
        this.savedFilterIds = [];
    }

    /**
     * Gets the default version of the query
     *
     * @param noDefaultDate  When TRUE, model will be built without a search date range defined
     */
    private getDefaultQuery(): MentionSearchQueryModel {
        const defaultQuery: MentionSearchQueryModel = new MentionSearchQueryModel();
        defaultQuery.dateQuery = DateQueryModel.getLast24HoursQuery();
        return defaultQuery;
    }

    /**
     * Gets the default version of the date facets
    */
    private getDefaultDateFacets(): DateFacetModel[] {
        const hourlyDateFacet: DateFacetModel = {
            field: DateFacetTypesEnum.created,
            sliceSizeInSecond: 3600 // 60 minutes * 60 seconds
        };

        const defaultDateFacets: DateFacetModel[] =  [
            hourlyDateFacet
        ];

        return defaultDateFacets;
    }

    /**
     * Gets the default version of the facets
    */
    private getDefaultFacets(): MentionSearchFacetTypesEnum[] {
        return MentionSearchCriteriaModel.mercurySearchFacets.slice();
    }

    /**
     * Compares properties on this object to provided searchCriteria model
     */
    equals(
        searchCriteria: MentionSearchCriteriaModel,
        ignoreDate: boolean = false,
        ignoreDateFacet: boolean = false,
        ignoreShowSimilar: boolean = false,
        ignorePagination: boolean = false,
        ignoreFacets: boolean = false
    ): boolean {
        if (!searchCriteria) {
            return false;
        }

        if (!ignoreDateFacet && (!StaticComparisonHelper.compareObjectsArraysForEquality(this.dateFacets, searchCriteria.dateFacets, 'field') ||
            !StaticComparisonHelper.compareObjectsArraysForEquality(this.dateFacets, searchCriteria.dateFacets, 'sliceSizeInSecond'))) {
            return false;
        }

        if (!ignoreFacets && !StaticComparisonHelper.compareObjectsArraysForEquality(this.facets, searchCriteria.facets)) {
            return false;
        }

        if (this.neoFacets.length !== searchCriteria.neoFacets.length) {
            return false;
        }

        for (let i = 0; i < this.neoFacets.length; i++) {
            if (!this.neoFacetEquals(this.neoFacets[i], searchCriteria.neoFacets[i])) {
                return false;
            }
        }

        if (!ignorePagination) {
            if (this.page !==  searchCriteria.page) {
                return false;
            }

            if (this.pageSize !== searchCriteria.pageSize) {
                return false;
            }

            if (this.sortField !== searchCriteria.sortField) {
                return false;
            }

            if (this.sortOrder !== searchCriteria.sortOrder) {
                return false;
            }
        }

        // showSimilar behaves like dateQuery: it drives the runtime duplicates filter rather than the
        // saved topic, so it is excluded from dirty/leave checks when ignoreShowSimilar is set.
        if (!ignoreShowSimilar && this.showSimilar !== searchCriteria.showSimilar) {
            return false;
        }

        if (!this.query.equals(searchCriteria.query, ignoreDate)) {
            return false;
        }

        if (this.filterQuery && !this.filterQuery.equals(searchCriteria.filterQuery, ignoreDate)) {
            return false;
        }
        if (!isEqual(this.savedFilterIds, searchCriteria.savedFilterIds)) {
            return false;
        }
        if (this.savedFilterIds) {
            if (this.savedFilterIds.length !== searchCriteria.savedFilterIds.length) {
                return false;
            }

            for (let i = 0; i < this.savedFilterIds.length; i++) {
                if (this.savedFilterIds[i] !== searchCriteria.savedFilterIds[i]) {
                    return false;
                }
            }
        }

        return true;
    }

    neoFacetEquals(thisNeoFacet: NeoFacetRequestApiModel, otherNeoFacet: NeoFacetRequestApiModel): boolean {
        if (thisNeoFacet.gap !== otherNeoFacet.gap) {
            return false;
        }

        if (thisNeoFacet.key !== otherNeoFacet.key) {
            return false;
        }

        if (thisNeoFacet?.range?.start !== otherNeoFacet?.range?.start) {
            return false;
        }

        if (thisNeoFacet?.range?.end !== otherNeoFacet?.range?.end) {
            return false;
        }

        if (thisNeoFacet.termField !== otherNeoFacet.termField) {
            return false;
        }

        if (thisNeoFacet.dateField !== otherNeoFacet.dateField) {
            return false;
        }

        if (
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.plainBucketRequests, otherNeoFacet.plainBucketRequests, 'field') ||
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.plainBucketRequests, otherNeoFacet.plainBucketRequests, 'key')
        ) {
            return false;
        }

        if (
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.aggregateBucketRequests, otherNeoFacet.aggregateBucketRequests, 'function') ||
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.aggregateBucketRequests, otherNeoFacet.aggregateBucketRequests, 'field') ||
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.aggregateBucketRequests, otherNeoFacet.aggregateBucketRequests, 'key')
        ) {
            return false;
        }

        if (
            !StaticComparisonHelper.compareObjectsArraysForEquality(
                thisNeoFacet.engagementBucketRequests, otherNeoFacet.engagementBucketRequests, 'engagementAge'
            ) ||
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.engagementBucketRequests, otherNeoFacet.engagementBucketRequests, 'field') ||
            !StaticComparisonHelper.compareObjectsArraysForEquality(thisNeoFacet.engagementBucketRequests, otherNeoFacet.engagementBucketRequests, 'key')
        ) {
            return false;
        }

        return true;
    }
}
